import React, { useEffect, useState } from "react";
import { API, http } from "@/lib/constants";
import { cn, formatLovelace } from "@/lib/format";

// ---------- Pool epoch-history table with active-stake bars ----------
export const PoolHistoryTable = ({ rows, amountMode, loading }) => {
  const [networkStake, setNetworkStake] = useState(null);
  useEffect(() => {
    let cancelled = false;
    http
      .get(`${API}/network/stats`)
      .then((res) => { if (!cancelled) setNetworkStake(res.data?.active_stake_total || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (loading && (!rows || rows.length === 0)) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-[52px] rounded-xl border border-primary/15 bg-card/30 animate-pulse" />
        ))}
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return <div className="text-[12px] text-muted-foreground/60 mono py-6">no history</div>;
  }
  const total = networkStake ? BigInt(networkStake) : 0n;
  const K = 500; // optimal pool count (k) — saturation reference
  const cols = "grid grid-cols-[64px_minmax(150px,1fr)_minmax(150px,1.6fr)_90px] gap-5 items-center";
  return (
    <div data-testid="pool-history-table" className="rounded-2xl border border-primary/20 bg-card/30 backdrop-blur-md overflow-hidden">
      <div className={cn(cols, "px-5 py-3 border-b border-primary/15 text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono")}>
        <span>Epoch</span>
        <span className="text-right">Active stake</span>
        <span>Saturation</span>
        <span className="text-right">Delegators</span>
      </div>
      <div className="divide-y divide-primary/10">
        {rows.map((r, i) => {
          const v = BigInt(r.active_stake || 0);
          // Exact per-epoch saturation from backend; fallback to client estimate.
          const sharePct = total > 0n ? Number((v * 1000000n) / total) / 10000 : 0;
          const pct = r.saturation != null ? r.saturation * 100 : sharePct * K;
          const hasData = r.saturation != null || total > 0n;
          const over = pct > 100;
          return (
            <div key={`${r.epoch}-${i}`} data-testid={`pool-history-row-${i}`} className={cn(cols, "px-5 py-3 hover:bg-primary/[0.03] transition-colors")}>
              <span className="font-mono text-[13px] tabnum text-foreground">{r.epoch}</span>
              <span className="font-mono text-[13px] tabnum text-foreground/90 text-right">{formatLovelace(r.active_stake, amountMode)}</span>
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex-1 h-2.5 rounded-full bg-primary/10 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, Math.max(2, pct))}%`,
                      background: over ? "#fb7185" : "hsl(264 100% 68%)",
                      boxShadow: over ? "0 0 10px rgba(251,113,133,0.7)" : "0 0 10px rgba(157,92,255,0.7)",
                    }}
                  />
                </div>
                <span className="font-mono text-[11px] tabnum text-muted-foreground/80 w-12 text-right shrink-0">
                  {hasData ? `${pct.toFixed(1)}%` : "—"}
                </span>
              </div>
              <span className="font-mono text-[13px] tabnum text-foreground/90 text-right">{r.delegators_count ?? 0}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
